import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExpand, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { VARPhoto } from './usePhotoLoader';
import './VarPhotoGrid.scss';

interface VarPhotoGridProps {
  photos: VARPhoto[];
  editable?: boolean;
  onDelete?: (photo: VARPhoto) => void;
  emptyMessage?: string;
}

const BOOLEAN_FLAGS: { key: keyof VARPhoto; label: string }[] = [
  { key: 'disrepair', label: 'Disrepair' },
  { key: 'forestFuels', label: 'Forest Fuels' },
  { key: 'fuelMitigation', label: 'Fuel Mitigation' },
  { key: 'fuelProximity', label: 'Fuel Proximity' },
  { key: 'hazmat', label: 'Hazmat' },
];

const VarPhotoGrid: React.FC<VarPhotoGridProps> = ({
  photos,
  editable = false,
  onDelete,
  emptyMessage = 'No photos',
}) => {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const handleDeleteClick = useCallback((photo: VARPhoto, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingId(photo.id);
  }, []);

  const confirmDelete = useCallback((photo: VARPhoto, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingId(null);
    onDelete?.(photo);
  }, [onDelete]);

  const cancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingId(null);
  }, []);

  if (photos.length === 0) {
    return <p className="var-photo-grid__empty">{emptyMessage}</p>;
  }

  return (
    <>
      <div className="var-photo-grid">
        {photos.map((photo, idx) => {
          const isConfirming = confirmingId === photo.id;
          return (
            <div key={photo.id} className="var-photo-grid__tile">
              <button
                type="button"
                className="var-photo-grid__thumb-btn"
                onClick={() => setLightboxIndex(idx)}
                aria-label={`Open ${photo.title}`}
              >
                {photo.thumbnailUrl ? (
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.title}
                    className="var-photo-grid__thumb"
                    loading="lazy"
                  />
                ) : (
                  <div className="var-photo-grid__thumb-missing">No image</div>
                )}
                <span className="var-photo-grid__expand-icon">
                  <FontAwesomeIcon icon={faExpand} />
                </span>
              </button>

              {editable && (
                <div className="var-photo-grid__actions">
                  {!isConfirming ? (
                    <button
                      type="button"
                      className="var-photo-grid__action var-photo-grid__action--delete"
                      onClick={(e) => handleDeleteClick(photo, e)}
                      title="Delete photo"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  ) : (
                    <div className="var-photo-grid__confirm">
                      <span>Delete?</span>
                      <button
                        type="button"
                        className="var-photo-grid__confirm-yes"
                        onClick={(e) => confirmDelete(photo, e)}
                      >Yes</button>
                      <button
                        type="button"
                        className="var-photo-grid__confirm-no"
                        onClick={cancelDelete}
                      >No</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {lightboxIndex !== null && photos[lightboxIndex] && (
        <Lightbox
          photo={photos[lightboxIndex]}
          onClose={() => setLightboxIndex(null)}
          onPrev={lightboxIndex > 0 ? () => setLightboxIndex(lightboxIndex - 1) : undefined}
          onNext={lightboxIndex < photos.length - 1 ? () => setLightboxIndex(lightboxIndex + 1) : undefined}
          counter={`${lightboxIndex + 1} / ${photos.length}`}
        />
      )}
    </>
  );
};

interface LightboxProps {
  photo: VARPhoto;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  counter: string;
}

const Lightbox: React.FC<LightboxProps> = ({ photo, onClose, onPrev, onNext, counter }) => {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  const activeFlags = BOOLEAN_FLAGS.filter((f) => photo[f.key] === true);

  // Portal to body to escape transformed ancestors (Mapbox popup) so
  // position:fixed resolves against the viewport.
  return createPortal(
    <div className="var-photo-lightbox" onClick={onClose}>
      <div className="var-photo-lightbox__inner" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="var-photo-lightbox__close"
          onClick={onClose}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <img
          src={photo.fullUrl || photo.thumbnailUrl || ''}
          alt={photo.title}
          className="var-photo-lightbox__img"
        />
        <div className="var-photo-lightbox__caption">
          <span>{photo.title}</span>
          <span>{counter}</span>
        </div>
        {(photo.cardinal || activeFlags.length > 0) && (
          <div className="var-photo-lightbox__meta">
            {photo.cardinal && (
              <span className="var-photo-lightbox__chip var-photo-lightbox__chip--cardinal">{photo.cardinal}</span>
            )}
            {activeFlags.map((f) => (
              <span key={f.key} className="var-photo-lightbox__chip">{f.label}</span>
            ))}
          </div>
        )}
        {photo.description && (
          <p className="var-photo-lightbox__desc">{photo.description}</p>
        )}
        {onPrev && (
          <button type="button" className="var-photo-lightbox__nav var-photo-lightbox__nav--prev" onClick={onPrev}>‹</button>
        )}
        {onNext && (
          <button type="button" className="var-photo-lightbox__nav var-photo-lightbox__nav--next" onClick={onNext}>›</button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default VarPhotoGrid;
